/**
 * 编辑工作区模态框组件
 *
 * 提供工作区名称、简介、标签编辑和删除确认功能
 */

import React, { useState, useCallback, useEffect } from 'react';
import type { Workspace } from '../../types/workspace';
import { useWorkspaceStore } from '../../store/workspaceStore';

interface EditWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspace: Workspace | null;
  onUpdateSuccess?: () => void;
}

export const EditWorkspaceModal: React.FC<EditWorkspaceModalProps> = ({
  isOpen,
  onClose,
  workspace,
  onUpdateSuccess
}) => {
  const { updateWorkspace, deleteWorkspace, error, clearError } = useWorkspaceStore();

  // 表单状态
  const [name, setName] = useState('');
  const [summary, setSummary] = useState('');
  const [mode, setMode] = useState<'edit' | 'delete'>('edit');
  const [localError, setLocalError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 重置表单当workspace变化时
  useEffect(() => {
    if (workspace) {
      setName(workspace.name);
      setSummary(workspace.summary || '');
      setLocalError(null);
    }
  }, [workspace]);

  // 切换模式时清除错误
  useEffect(() => {
    setLocalError(null);
    clearError();
  }, [mode, clearError]);

  // 验证名称
  const validateName = useCallback((value: string): { valid: boolean; message: string } => {
    if (!value.trim()) {
      return { valid: false, message: '工作区名称不能为空' };
    }
    if (value.trim().length > 20) {
      return { valid: false, message: '工作区名称长度不能超过 20 字符' };
    }
    if (!/[\u4e00-\u9fff]/.test(value.trim())) {
      return { valid: false, message: '工作区名称必须包含至少一个汉字' };
    }
    return { valid: true, message: '' };
  }, []);

  // 处理保存
  const handleSave = useCallback(async () => {
    if (!workspace) return;

    const validation = validateName(name);
    if (!validation.valid) {
      setLocalError(validation.message);
      return;
    }

    setIsSubmitting(true);
    setLocalError(null);

    try {
      // 使用 Cookie 认证更新工作区
      await updateWorkspace(workspace.id, { 
        name: name.trim(),
        summary: summary.trim() || undefined
      });
      onClose();
      onUpdateSuccess?.();
    } catch (err) {
      setLocalError('更新失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  }, [workspace, name, summary, validateName, updateWorkspace, onClose, onUpdateSuccess]);

  // 处理删除
  const handleDelete = useCallback(async () => {
    if (!workspace) return;

    setIsSubmitting(true);
    setLocalError(null);

    try {
      // 使用 Cookie 认证删除工作区
      await deleteWorkspace(workspace.id);
      onClose();
      onUpdateSuccess?.();
    } catch (err) {
      setLocalError('删除失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  }, [workspace, deleteWorkspace, onClose, onUpdateSuccess]);

  // 按ESC关闭
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !workspace) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      {/* 背景点击关闭 */}
      <div
        className="absolute inset-0"
        onClick={onClose}
      />

      {/* 模态框内容 */}
      <div className="relative bg-white rounded-2xl shadow-md w-full max-w-lg mx-4 animate-fade-in max-h-[90vh] overflow-y-auto">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            {mode === 'edit' ? '编辑工作区' : '删除工作区'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 transition-colors"
            disabled={isSubmitting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 模式切换标签 */}
        <div className="flex px-6 pt-4 gap-2">
          <button
            onClick={() => setMode('edit')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              mode === 'edit'
                ? 'bg-ink-800 text-white'
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
            disabled={isSubmitting}
          >
            编辑信息
          </button>
          <button
            onClick={() => setMode('delete')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              mode === 'delete'
                ? 'bg-red-500 text-white'
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
            disabled={isSubmitting}
          >
            删除工作区
          </button>
        </div>

        {/* 内容区域 */}
        <div className="p-6">
          {mode === 'edit' ? (
            /* 编辑模式 */
            <div className="space-y-5">
              {/* 工作区名称 */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  工作区名称
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  placeholder="请输入工作区名称"
                  maxLength={20}
                  disabled={isSubmitting}
                />
                <p className="mt-1 text-sm text-gray-700">
                  {name.length}/20 字符
                </p>
              </div>

              {/* 小说简介 */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  小说简介
                </label>
                <textarea
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent resize-none"
                  placeholder="请输入小说简介（可选）"
                  rows={4}
                  maxLength={500}
                  disabled={isSubmitting}
                />
                <p className="mt-1 text-sm text-gray-700">
                  {summary.length}/500 字符
                </p>
              </div>

              {/* 小说标签 */}
              {workspace.tags && workspace.tags.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    小说标签
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {workspace.tags.map(tag => (
                      <span
                        key={tag.tag_id}
                        className="px-3 py-1 rounded-full text-sm bg-paper-100 text-paper-800"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* 删除模式 */
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                确认删除工作区？
              </h3>
              <p className="text-gray-800 mb-4">
                《{workspace.name}》
              </p>
              <p className="text-sm text-gray-700">
                此操作不可撤销，工作区及其所有内容将被永久删除。
              </p>
            </div>
          )}

          {/* 错误提示 */}
          {(localError || error) && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
              {localError || error}
            </div>
          )}

          {/* 加载状态 */}
          {isSubmitting && (
            <div className="mt-4 text-center">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-500"></div>
              <p className="mt-2 text-sm text-gray-800">
                {mode === 'edit' ? '保存中...' : '删除中...'}
              </p>
            </div>
          )}
        </div>

        {/* 底部按钮 */}
        <div className="flex justify-end gap-3 px-6 pb-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md font-medium hover:bg-gray-200 transition-colors"
            disabled={isSubmitting}
          >
            取消
          </button>
          {mode === 'edit' ? (
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-ink-800 text-white rounded-md font-medium shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              保存
            </button>
          ) : (
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-500 text-white rounded-md font-medium hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              确认删除
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditWorkspaceModal;
